import { Checkbox, HStack, Modal, VStack } from '@musinsa/mwms-ds'
import Button from '@/components/content/Button'
import { Controller, useForm } from 'react-hook-form'
import { ADJUSTMENT_CHARGE_TYPE, AdjustmentAmountReq, AdjustmentReasonType } from '@/services/settlement/settlementManagement/types'
import styled, { css } from 'styled-components'
import { Divider } from '@/components/content/Divider'
import SelectBox from '@/components/content/SelectBox'
import Input from '@/components/content/Input'
import { useGetAdjustmentTypes } from '@/services/settlement/settlementManagement/useSettlementManagementQueries'
import { SegmentTabs } from '@/components/content/SegmentTabs'
import TextArea from '@/components/content/TextArea'
import { AdjustmentFormValues, getAdjustmentSchema } from '../schemas/adjustmentFormSchema'
import { zodResolver } from '@hookform/resolvers/zod'
import MAlert from '@/utils/modal/MAlert'
import Text from '@/components/content/Text'
import { tokens } from '@musinsa/layer-tokens/desktop'

const deductionDisabledLabels = new Set<AdjustmentReasonType>(['RETURN_COST', 'OTHER_SHIPPING', 'VAS_WORK'])
const chargeDisabledLabels = new Set<AdjustmentReasonType>(['PROMOTION_SUPPORT', 'DAMAGE_COMPENSATION'])
const reasonRequiredFields = new Set<AdjustmentReasonType>(['MANUAL_ADJUST', 'OTHER_SHIPPING', 'ETC'])

interface AdjustmentModalProps extends Pick<AdjustmentFormValues, 'settlementMonth' | 'partnerId'> {
  onClose: () => void
  onConfirm: (formValues: AdjustmentAmountReq) => Promise<void>
}

export function AdjustmentModal({ partnerId, settlementMonth, onClose, onConfirm }: AdjustmentModalProps) {
  const { options } = useGetAdjustmentTypes()

  const initialValues: Omit<AdjustmentFormValues, 'adjustmentReasonType' | 'adjustmentAmount'> = {
    partnerId,
    settlementMonth,
    settlementBaseType: 'PARTNER', // NOTE: 현재 파트너만 대상이여서 파트너 고정
    adjustmentChargeType: ADJUSTMENT_CHARGE_TYPE[0],
    adjustmentReason: '',
    isContinue: false,
  }

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    reset,
    watch,
    setValue,
    clearErrors,
  } = useForm<AdjustmentFormValues>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: zodResolver(getAdjustmentSchema(isReasonRequired)),
  })

  const registerBtnDisabled = !(isDirty && isValid)

  const handleRegisterAdjustment = async (formValues: AdjustmentFormValues) => {
    const { isContinue, adjustmentCount, ...values } = formValues

    try {
      await onConfirm({
        ...values,
        adjustmentAmount: Number(values.adjustmentAmount),
        ...(isDeductionDisabled(values.adjustmentReasonType) && Boolean(adjustmentCount) ? { adjustmentCount: Number(adjustmentCount) } : {}),
      })
      if (!isContinue) {
        onClose()
        return
      }
      reset({
        ...initialValues,
        isContinue,
      })
    } catch {
      MAlert.show(`일시적인 오류로 처리에 실패했어요.\n잠시 후 다시 시도해주세요.`, {
        subContent: '반복적으로 실패할 경우, 관리자에게 문의해 주세요.',
      })
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="기타조정비 등록"
      content={
        <VStack gap={24}>
          <VStack gap={16}>
            <FieldTitle>적용 대상</FieldTitle>
            <Row>
              <Label>정산 월</Label>
              <Text kind="Body_14_regular">{settlementMonth} 정산서</Text>
            </Row>
            <Row>
              <Label>업체아이디</Label>
              <Text kind="Body_14_regular">{partnerId}</Text>
            </Row>
          </VStack>

          <Divider />

          <VStack gap={16}>
            <FieldTitle>기타 조정금액</FieldTitle>
            <Row>
              <Label isRequired>조정항목</Label>
              <Controller
                render={({ field }) => (
                  <SelectBox
                    block
                    options={options}
                    placeholder="조정액 항목을 선택해 주세요."
                    {...field}
                    onChange={(value) => {
                      const chargeType = isDeductionDisabled(value) ? 'CHARGE' : 'DEDUCTION'

                      field.onChange(value)
                      setValue('adjustmentChargeType', chargeType)
                      if (!isReasonRequired(value)) {
                        clearErrors('adjustmentReason')
                      }
                    }}
                  />
                )}
                name="adjustmentReasonType"
                control={control}
              />
            </Row>
            <Row>
              <Label isRequired>조정금액</Label>
              <VStack gap={4} style={{ width: '100%' }}>
                <Controller
                  render={({ field }) => (
                    <SegmentTabs value={field.value} onTabChange={field.onChange}>
                      <SegmentTabs.Item label="공제(-)" value="DEDUCTION" disabled={isDeductionDisabled(watch('adjustmentReasonType'))} />
                      <SegmentTabs.Item label="청구(+)" value="CHARGE" disabled={chargeDisabledLabels.has(watch('adjustmentReasonType'))} />
                    </SegmentTabs>
                  )}
                  name="adjustmentChargeType"
                  control={control}
                />
                <Controller
                  render={({ field, formState }) => (
                    <Input
                      block
                      placeholder={watch('adjustmentReasonType') ? '조정금액을 입력해 주세요.' : '조정항목을 먼저 선택해 주세요.'}
                      unitLabel="원"
                      error={formState.errors.adjustmentAmount?.message}
                      disabled={!watch('adjustmentReasonType')}
                      {...field}
                      onChange={(value) => {
                        const onlyNumbers = value.replace(/[^0-9]/g, '')

                        const processedValue =
                          onlyNumbers.length > 1 && onlyNumbers.startsWith('0') ? onlyNumbers.replace(/^0+/, '') || '0' : onlyNumbers

                        field.onChange(processedValue)
                      }}
                    />
                  )}
                  name="adjustmentAmount"
                  control={control}
                />
              </VStack>
            </Row>
            {isDeductionDisabled(watch('adjustmentReasonType')) && (
              <Row>
                <Label>발생건수</Label>
                <Controller
                  render={({ field, formState }) => (
                    <Input
                      block
                      placeholder="발생건수를 입력해 주세요."
                      unitLabel="건"
                      error={formState.errors.adjustmentCount?.message}
                      {...field}
                      onChange={(value) => {
                        const onlyNumbers = value.replace(/[^0-9]/g, '')

                        const processedValue =
                          onlyNumbers.length > 1 && onlyNumbers.startsWith('0') ? onlyNumbers.replace(/^0+/, '') || '0' : onlyNumbers

                        field.onChange(processedValue)
                      }}
                    />
                  )}
                  name="adjustmentCount"
                  control={control}
                />
              </Row>
            )}
            <Row>
              <Label isRequired={isReasonRequired(watch('adjustmentReasonType'))}>상세사유</Label>
              <Controller
                render={({ field }) => <TextArea placeholder="상세사유를 입력해 주세요." minHeight={80} cols={40} maxLength={200} {...field} />}
                name="adjustmentReason"
                control={control}
              />
            </Row>
          </VStack>
        </VStack>
      }
      customFooter={
        <HStack
          justify="space-between"
          style={{
            width: '100vw',
          }}
        >
          <Controller
            name="isContinue"
            control={control}
            render={({ field: { value, ...rest } }) => <Checkbox size="sm" labelText="계속 등록하기" checked={value} {...rest} />}
          />
          <HStack gap={8}>
            <Button fit kind="secondary" onClick={onClose}>
              취소
            </Button>
            <Button fit type="submit" kind="primary_light" disabled={registerBtnDisabled} onClick={handleSubmit(handleRegisterAdjustment)}>
              등록
            </Button>
          </HStack>
        </HStack>
      }
    />
  )
}
const isDeductionDisabled = (value: AdjustmentReasonType) => deductionDisabledLabels.has(value)
const isReasonRequired = (value: AdjustmentReasonType) => reasonRequiredFields.has(value)

const FieldTitle = styled('h3')`
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 128px 1fr;
  gap: 32px;
`

const Label = styled.span<{ isRequired?: boolean }>`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;

  ${({ isRequired }) =>
    isRequired &&
    css`
      &:after {
        content: '*';
        position: relative;
        top: 8px;
        left: 2px;
        font-size: 20px;
        color: ${tokens.layerDesktop['fg-accent']};
      }
    `}
`
